/**
 * AdamVoicePicker - [ADAM AUDIO batch 2 item 4] commissioner voice choice.
 *
 * One component, two mounts: the lobby (below the music picker) and the
 * in-draft commissioner controls. The selection is a SERVER-SIDE per-league
 * setting (`League.settings.adam_voice_id`), so switching mid-draft is a
 * next-line handoff for every client at once - the line already playing
 * finishes in the old voice, everything after it (cached or live) resolves
 * to the new one. Nothing here syncs voice state between clients; the
 * server is the single source of truth.
 *
 * Preview is the ONE path where a client names a voice_id, and the backend
 * whitelists it to these two.
 */
import { useState } from "react";
import axios from "axios";
import { Mic, Play, Check, Loader2 } from "lucide-react";
import { enqueue } from "../../audio/auctioneerAudio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ADAM_VOICES = [
  { key: "voice_1", id: "q0IMILNRPxOgtBTS4taI", label: "Voice 1", blurb: "Classic PGF Adam" },
  { key: "voice_2", id: "DLGMQjlelXT7GZX5lnKl", label: "Voice 2", blurb: "Alternate broadcast" },
];

const PREVIEW_LINE = "Going once! Going twice! Sold, to the highest bidder!";

export default function AdamVoicePicker({
  leagueId,
  selectedVoiceId,
  isDark = true,
  onSelected,
}) {
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState("");
  const selected = selectedVoiceId || ADAM_VOICES[0].id;

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("pgf_token")}`,
  });

  const select = async (voice) => {
    setBusyKey(`select-${voice.key}`);
    setError("");
    try {
      await axios.post(
        `${API}/commissioner/${leagueId}/set-adam-voice`,
        { voice_id: voice.id },
        { headers: authHeaders() },
      );
      onSelected?.(voice.id);
      // The per-league kickoff clip is voice-keyed - warm it in the new
      // voice now so the countdown never waits on a render.
      fetch(`${API}/tts/draft-kickoff/${leagueId}`).catch(() => {});
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not switch voice");
    } finally {
      setBusyKey(null);
    }
  };

  const preview = async (voice) => {
    setBusyKey(`preview-${voice.key}`);
    setError("");
    try {
      const { data } = await axios.post(`${API}/tts/speak`, {
        text: PREVIEW_LINE,
        voice_id: voice.id,
      });
      if (data?.audio_b64) {
        await enqueue({ group: "VOICE_PREVIEW", audio_b64: data.audio_b64 });
      }
    } catch (e) {
      setError("Preview unavailable");
    } finally {
      setBusyKey(null);
    }
  };

  const cardBg = isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)";
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const sub = isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)";

  return (
    <div data-testid="adam-voice-picker" className="mt-4">
      <div className="flex items-center gap-2 mb-2">
        <Mic size={13} className="text-[#FF6B00]" />
        <p
          className="font-display font-black text-[10px] uppercase tracking-[0.28em]"
          style={{ color: sub }}
        >
          Adam's Voice
        </p>
      </div>

      <div className="space-y-2">
        {ADAM_VOICES.map((voice) => {
          const isSelected = voice.id === selected;
          return (
            <div
              key={voice.key}
              data-testid={`adam-voice-row-${voice.key}`}
              className="flex items-center gap-2 rounded-xl px-3 py-2"
              style={{
                background: cardBg,
                border: `1px solid ${isSelected ? "#FF6B00" : border}`,
              }}
            >
              <div className="flex-1 min-w-0">
                <p
                  className="font-display font-bold text-sm uppercase truncate"
                  style={{ color: isDark ? "#F5F5F5" : "#0A0A0A" }}
                >
                  {voice.label}
                </p>
                <p className="font-body text-[11px] truncate" style={{ color: sub }}>
                  {voice.blurb}
                </p>
              </div>

              <button
                type="button"
                data-testid={`adam-voice-preview-${voice.key}`}
                onClick={() => preview(voice)}
                disabled={busyKey !== null}
                className="rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-40"
                style={{ border: `1px solid ${border}`, color: sub }}
                aria-label={`Preview ${voice.label}`}
              >
                {busyKey === `preview-${voice.key}`
                  ? <Loader2 size={13} className="animate-spin" />
                  : <Play size={13} />}
              </button>

              <button
                type="button"
                data-testid={`adam-voice-select-${voice.key}`}
                onClick={() => select(voice)}
                disabled={busyKey !== null || isSelected}
                className="rounded-lg px-3 py-1.5 font-display font-bold text-[11px] uppercase tracking-wider transition-colors disabled:opacity-60"
                style={
                  isSelected
                    ? { background: "#FF6B00", color: "#FFFFFF" }
                    : { border: `1px solid ${border}`, color: isDark ? "#F5F5F5" : "#0A0A0A" }
                }
              >
                {busyKey === `select-${voice.key}` ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : isSelected ? (
                  <span className="flex items-center gap-1"><Check size={12} /> Selected</span>
                ) : (
                  "Select"
                )}
              </button>
            </div>
          );
        })}
      </div>

      {error && (
        <p data-testid="adam-voice-error" className="font-body text-[11px] mt-2 text-[#FF4D4D]">
          {error}
        </p>
      )}
    </div>
  );
}
